/**
 * MaskedCell (§16.3): list views default to masked contact / ID / payout data. "Unmask" asks for a purpose,
 * steps up (identity.unmask ∈ STEP_UP_PERMISSIONS) and calls `POST /v1/admin/identity/unmask`; the value is
 * shown until `validUntil`, then re-masked. The unmask is audited server-side.
 */
import type { UnmaskRequest } from '@windrop/contracts';
import { Button, Field, Input, Modal, useAnnounce } from '@windrop/ui';
import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Can } from '../auth/guards';
import { useStepUp } from '../auth/step-up';
import { identityApi } from '../lib/api/admin';
import { isApiError } from '../lib/api/client';

export interface MaskedCellProps {
  masked: string | null;
  subject: UnmaskRequest['subject'];
  /** What is being unmasked, for the dialog ("WhatsApp number"). */
  what?: string;
  /** Test hook / override for the API call. */
  unmask?: (body: UnmaskRequest) => Promise<{ value: string; validUntil: string; auditId: string }>;
}

export function MaskedCell({
  masked,
  subject,
  what = 'value',
  unmask = identityApi.unmask,
}: MaskedCellProps) {
  const { withStepUp } = useStepUp();
  const { announce } = useAnnounce();
  const [open, setOpen] = useState(false);
  const [purpose, setPurpose] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<{ value: string; validUntil: string } | null>(null);

  useEffect(() => {
    if (!revealed) return;
    const ms = Math.max(0, Date.parse(revealed.validUntil) - Date.now());
    const t = setTimeout(() => {
      setRevealed(null);
      announce(`${what} re-masked`);
    }, ms);
    return () => {
      clearTimeout(t);
    };
  }, [revealed, what, announce]);

  if (masked === null) return <span className="wd-muted">—</span>;

  const submit = async () => {
    if (purpose.trim().length < 10) {
      setError('Give a purpose of at least 10 characters; it is written to the audit trail.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await withStepUp(() => unmask({ subject, purpose: purpose.trim() }), {
        permission: 'identity.unmask',
        reason: `Unmasking a ${what}.`,
      });
      setRevealed({ value: res.value, validUntil: res.validUntil });
      setOpen(false);
      setPurpose('');
      announce(`${what} unmasked until ${new Date(res.validUntil).toLocaleTimeString()}`);
    } catch (e) {
      setError(
        isApiError(e)
          ? e.message
          : e instanceof Error && e.name === 'StepUpCancelled'
            ? null
            : 'Unmask failed.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="wdc-masked">
      <code data-testid="masked-value">{revealed ? revealed.value : masked}</code>
      {revealed ? (
        <button
          type="button"
          className="wd-btn wd-touch"
          data-variant="quiet"
          onClick={() => setRevealed(null)}
          aria-label={`Re-mask ${what}`}
        >
          <EyeOff size={16} aria-hidden="true" />
        </button>
      ) : (
        <Can permission="identity.unmask">
          <button
            type="button"
            className="wd-btn wd-touch"
            data-variant="quiet"
            onClick={() => setOpen(true)}
            aria-label={`Unmask ${what}`}
          >
            <Eye size={16} aria-hidden="true" />
          </button>
        </Can>
      )}
      <Modal
        open={open}
        onOpenChange={setOpen}
        title={`Unmask ${what}`}
        description="Unmasking is permissioned, purpose-bound and audited (§16.3). The value re-masks automatically."
      >
        <div className="wd-stack">
          <Field
            label="Purpose"
            required
            hint="Why you need the unmasked value, e.g. the claim or support case you are working."
          >
            <Input
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              data-testid="unmask-purpose"
            />
          </Field>
          {error ? (
            <p role="alert" className="text-error">
              {error}
            </p>
          ) : null}
          <div className="wd-btn-group">
            <Button onClick={submit} loading={busy} loadingLabel="Unmasking">
              Unmask for this purpose
            </Button>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </span>
  );
}
